import { faDownload, faTrashCan } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaSort, FaSortDown, FaSortUp } from "react-icons/fa";
import { useNavigate, useSearchParams } from "react-router";
import { loadProject } from "../../client/Navigation/Settings";
import Pagination from "../../components/Pagination/Pagination";
import { useDeleteProjectMutation, useGetAllProjectsQuery } from "../../services/API/titanApi";
import { BemessungenMikropfaehle } from "../../store/slices/bemessungenMikropfaehleSlice";
import "./ProjectsList.scss";
import { useDispatch, useSelector } from "react-redux";
import { selectProjectId, setProjectId, setProjectName } from "../../store/slices/projectSlice";
import { toast } from "react-toastify";
import DeleteConfirmation from "../UsersList/DeleteConfirmation";

// https://stackoverflow.com/questions/1129216/sort-array-of-objects-by-string-property-value
function dynamicSort(property) {
  var sortOrder = 1;
  if (property[0] === "-") {
    sortOrder = -1;
    property = property.substr(1);
  }

  return function (a, b) {
    /* next line works with strings and numbers,
     * and you may want to customize it to your needs
     */
    var result = a?.[property] < b?.[property] ? -1 : a?.[property] > b?.[property] ? 1 : 0;
    return result * sortOrder;
  };
}

const ProjectsList = () => {
  const { i18n, t } = useTranslation("translation");
  const currenProjectId = useSelector(selectProjectId);
  const dispatch = useDispatch();
  const [search, setSearch] = useState("");
  const handleSearch = (e) => {
    setSearch(e.target.value);
  };
  function getbemessungenMikropfaehleText(bemessungenMikropfaehle) {
    switch (bemessungenMikropfaehle) {
      case BemessungenMikropfaehle.EUROCODE_7:
        return t("deckblatt.eurocode");
        break;
      case BemessungenMikropfaehle.GERMANY:
        return t("deckblatt.germany");
        break;
      case BemessungenMikropfaehle.AASHTO:
        return t("deckblatt.aashto");
        break;
      case BemessungenMikropfaehle.FHWA:
        return t("deckblatt.fhwa");
        break;
    }
  }

  const { data: projects } = useGetAllProjectsQuery();
  const [deleteProject] = useDeleteProjectMutation();
  const [formState, setFormState] = useState({});

  let PageSize = 10;
  const [currentPage, setCurrentPage] = useState(1);
  const [sortedBy, setSortedBy] = useState("");

  function setSortBy(params) {
    if (params === sortedBy) {
      let sort = "-" + params;
      setSortedBy(sort);
      return;
    }
    setSortedBy(params);
  }

  const [fullTableData, setFullTableData] = useState([]);
  const currentTableData = useMemo(() => {
    const firstPageIndex = (currentPage - 1) * PageSize;
    const lastPageIndex = firstPageIndex + PageSize;
    return fullTableData?.slice(firstPageIndex, lastPageIndex);
  }, [currentPage, PageSize, fullTableData]);

  useEffect(() => {
    //filter
    let modifiedArray = projects?.filter(
      (item) =>
        item?.selectedTragglied?.title?.toLowerCase().includes(search.toLowerCase()) ||
        item?.projectName?.toLowerCase().includes(search.toLowerCase()) ||
        getbemessungenMikropfaehleText(item?.bemessungenMikropfaehle.value)
          .toLowerCase()
          .includes(search.toLowerCase())
    );

    // sort
    if (sortedBy !== "") {
      modifiedArray.sort(dynamicSort(sortedBy));
    }
    setFullTableData(modifiedArray);
  }, [search, sortedBy, projects]);

  const [showDelete, setShowDelete] = useState(false);
  const handleDeleteShow = (project) => {
    setFormState({ id: project._id, ...project });
    setShowDelete(true);
  };
  const handleDeleteClose = () => setShowDelete(false);
  const handleDelete = async () => {
    const id = formState.id;
    try {
      await deleteProject({ id: formState.id })
        .unwrap()
        .then((payload) => {
          if (currenProjectId === id) {
            dispatch(setProjectId(""));
            dispatch(setProjectName(""));
          }
          toast.success(t("projectList.success.removed"));
        });
    } catch (error) {
      console.error(error);
      toast.error(t("projectList.error.internalError"));
    } finally {
      setShowDelete(false);
    }
  };

  const handleLoadShow = (project) => {
    loadProject({ id: project._id });
    toast.success(t("projectList.success.loaded"));
    navigate("/application");
  };

  const navigate = useNavigate();

  function SortIcon(value) {
    if (sortedBy === value) return <FaSortUp />;
    if (sortedBy === "-" + value) return <FaSortDown />;
    return <FaSort />;
  }
  useEffect(() => {
    if (currentTableData && currentTableData.length === 0) {
      if (currentPage > 1) {
        setCurrentPage(currentPage - 1);
      }
    }
  }, [currentTableData]);
  return (
    <>
      <div className="Users">
        <div className="container-fluid">
          <div className="table-wrapper">
            <div className="table-title">
              <div className="row">
                <div className="col-sm-6">
                  <h2>{t("projectList.title")}</h2>
                </div>

                <div className="col-sm-6">
                  <label className="float-end" htmlFor="search">
                    <span className="me-2">{t("projectList.search")}</span>
                    <input id="search" type="text" onChange={handleSearch} />
                  </label>
                </div>
              </div>
            </div>

            <div className="table-responsive">
              <table className="table table-striped table-hover">
                <thead>
                  <tr>
                    <th>
                      <span
                        className="searchHeader"
                        onClick={() => setSortBy("deckblatt.Project.title")}
                      >
                        {t("projectList.tableHeader.Project.title")}
                        {SortIcon("deckblatt.Project.title")}
                      </span>
                    </th>
                    <th>
                      <span
                        className="searchHeader"
                        onClick={() => setSortBy("bemessungenMikropfaehle.value")}
                      >
                        {t("navigation.BemessungenMikropfaehleSubtext")}
                        {SortIcon("bemessungenMikropfaehle.value")}
                      </span>
                    </th>
                    <th>
                      <span
                        className="searchHeader"
                        onClick={() => setSortBy("selectedTragglied.title")}
                      >
                        {t("aeussre.LoadCarryingElement")}
                        {SortIcon("selectedTragglied.title")}
                      </span>
                    </th>
                    <th>
                      <span className="searchHeader" onClick={() => setSortBy("createdAt")}>
                        {t("projectList.tableHeader.createdAt")}
                        {SortIcon("createdAt")}
                      </span>
                    </th>
                    <th>
                      <div className="searchHeader" onClick={() => setSortBy("updatedAt")}>
                        {t("projectList.tableHeader.updatedAt")}
                        {SortIcon("updatedAt")}
                      </div>
                    </th>
                    <th>{t("projectList.tableHeader.actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {currentTableData?.map((project, index) => {
                    return (
                      <tr key={index} className={project._id === currenProjectId ? "active" : null}>
                        <td>
                          {project.projectName !== ""
                            ? project.projectName
                            : project?.deckblatt?.Project?.title}
                        </td>
                        <td>
                          {getbemessungenMikropfaehleText(project?.bemessungenMikropfaehle.value)}
                        </td>
                        <td>
                          {project?.bemessungenMikropfaehle.value == BemessungenMikropfaehle.FHWA &&
                            project?.fhwa?.selectedCarryingElement?.name}
                          {project?.bemessungenMikropfaehle.value ==
                            BemessungenMikropfaehle.AASHTO &&
                            project?.aashto?.selectedCarryingElement?.name}
                          {project?.bemessungenMikropfaehle.value ==
                            BemessungenMikropfaehle.GERMANY && project?.selectedTragglied?.title}
                          {project?.bemessungenMikropfaehle.value ==
                            BemessungenMikropfaehle.EUROCODE_7 && project?.selectedTragglied?.title}
                        </td>
                        <td>
                          {new Intl.DateTimeFormat(i18n.language, {
                            month: "2-digit",
                            day: "numeric",
                            year: "2-digit",
                            hour: "2-digit",
                            minute: "2-digit",
                          }).format(new Date(project?.createdAt))}
                        </td>
                        <td>
                          {new Intl.DateTimeFormat(i18n.language, {
                            month: "2-digit",
                            day: "numeric",
                            year: "2-digit",
                            hour: "2-digit",
                            minute: "2-digit",
                          }).format(new Date(project?.updatedAt))}
                        </td>
                        <td>
                          <span
                            data-testid="project-load"
                            data-toggle="modal"
                            onClick={() => handleLoadShow(project)}
                          >
                            <FontAwesomeIcon
                              id="clickableAwesomeFont"
                              data-toggle="tooltip"
                              title={t("projectList.loadProject")}
                              icon={faDownload}
                              className="mx-2"
                            />
                          </span>

                          <span
                            data-testid="project-delete"
                            data-toggle="modal"
                            onClick={() => handleDeleteShow(project)}
                          >
                            <FontAwesomeIcon
                              id="clickableAwesomeFont"
                              data-toggle="tooltip"
                              title={t("projectList.deleteProject")}
                              color="red"
                              className="mx-2"
                              icon={faTrashCan}
                            />
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {currentTableData && (
              <Pagination
                className="pagination-bar"
                currentPage={currentPage}
                totalCount={fullTableData?.length}
                pageSize={PageSize}
                onPageChange={(page) => setCurrentPage(page)}
              />
            )}
          </div>
        </div>
        <DeleteConfirmation
          title={t("projectList.deleteModal.title")}
          show={showDelete}
          handleCancel={handleDeleteClose}
          handleSubmit={handleDelete}
        />
      </div>
    </>
  );
};
export default ProjectsList;
